import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import AppIcon from '../../../../components/AppIcon';
import {
  getGoalBlockers,
  createGoalBlocker,
  resolveGoalBlocker,
  deleteGoalBlocker,
  GoalBlockerView,
} from '../../../../api/PreformanceManagement/Goals';

interface BlockerPanelProps {
  goalId: string;
  isOpen: boolean;
  canEdit: boolean;
}

const severityColors: Record<string, string> = {
  Low: '#3b82f6',
  Medium: '#f59e0b',
  High: '#ef4444',
  Critical: '#7c2d12',
};

const severityBg: Record<string, string> = {
  Low: '#eff6ff',
  Medium: '#fef3c7',
  High: '#fef2f2',
  Critical: '#431407',
};

const BlockerPanel: React.FC<BlockerPanelProps> = ({ goalId, isOpen, canEdit }) => {
  const { t } = useTranslation();
  const [blockers, setBlockers] = useState<GoalBlockerView[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [resolveId, setResolveId] = useState<string | null>(null);
  const [resolveNote, setResolveNote] = useState('');
  const [formData, setFormData] = useState({ description: '', severity: 2, requiredResources: '' });
  const [submitting, setSubmitting] = useState(false);

  const loadBlockers = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getGoalBlockers(goalId);
      setBlockers(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('Failed to load blockers'));
    } finally {
      setLoading(false);
    }
  }, [goalId, t]);

  useEffect(() => {
    if (isOpen && goalId) {
      loadBlockers();
    }
  }, [isOpen, goalId, loadBlockers]);

  const handleAdd = async () => {
    if (!formData.description.trim()) return;
    setSubmitting(true);
    try {
      const newBlocker = await createGoalBlocker(goalId, {
        description: formData.description,
        severity: formData.severity,
        requiredResources: formData.requiredResources || undefined,
      });
      setBlockers(prev => [newBlocker, ...prev]);
      setFormData({ description: '', severity: 2, requiredResources: '' });
      setShowAddForm(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('Failed to add blocker'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleResolve = async (blockerId: string) => {
    if (!resolveNote.trim()) return;
    setSubmitting(true);
    try {
      const updated = await resolveGoalBlocker(blockerId, { resolutionNote: resolveNote });
      setBlockers(prev => prev.map(b => (b.id === blockerId ? updated : b)));
      setResolveId(null);
      setResolveNote('');
    } catch (err) {
      setError(err instanceof Error ? err.message : t('Failed to resolve blocker'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (blockerId: string) => {
    try {
      await deleteGoalBlocker(blockerId);
      setBlockers(prev => prev.filter(b => b.id !== blockerId));
    } catch (err) {
      setError(err instanceof Error ? err.message : t('Failed to delete blocker'));
    }
  };

  const formatDate = (dateStr: string) =>
    new Date(dateStr).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });

  const activeBlockers = blockers.filter(b => b.status === 'Active');
  const resolvedBlockers = blockers.filter(b => b.status === 'Resolved');

  if (loading) {
    return (
      <div className="flex justify-center py-4">
        <AppIcon name="Loader2" size={24} className="animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div data-testid="blocker-panel">
      {error && (
        <div className="bg-destructive/10 text-destructive text-sm p-2 rounded mb-3">{error}</div>
      )}

      {/* Add blocker button */}
      {canEdit && !showAddForm && (
        <button
          onClick={() => setShowAddForm(true)}
          className="w-full py-2 px-3 border-2 border-dashed border-border rounded-lg text-sm text-muted-foreground hover:border-primary hover:text-primary transition-colors flex items-center justify-center gap-2 mb-4"
          data-testid="add-blocker-btn"
        >
          <AppIcon name="Plus" size={16} />
          {t('Add Blocker')}
        </button>
      )}

      {/* Add form */}
      {showAddForm && (
        <div className="border border-border rounded-lg p-3 mb-4 space-y-3 bg-muted/30" data-testid="blocker-form">
          <div>
            <label className="block text-xs font-medium text-foreground mb-1">{t('Description')}</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              className="w-full px-2 py-1.5 border border-border rounded bg-background text-foreground text-sm resize-none"
              rows={2}
              placeholder={t('Describe the blocker...')}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-foreground mb-1">{t('Severity')}</label>
              <select
                value={formData.severity}
                onChange={(e) => setFormData(prev => ({ ...prev, severity: parseInt(e.target.value) }))}
                className="w-full px-2 py-1.5 border border-border rounded bg-background text-foreground text-sm"
              >
                <option value={1}>{t('Low')}</option>
                <option value={2}>{t('Medium')}</option>
                <option value={3}>{t('High')}</option>
                <option value={4}>{t('Critical')}</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-foreground mb-1">{t('Required Resources')}</label>
              <input
                type="text"
                value={formData.requiredResources}
                onChange={(e) => setFormData(prev => ({ ...prev, requiredResources: e.target.value }))}
                className="w-full px-2 py-1.5 border border-border rounded bg-background text-foreground text-sm"
                placeholder={t('Optional')}
              />
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <button
              onClick={() => setShowAddForm(false)}
              className="px-3 py-1.5 text-xs text-muted-foreground hover:bg-muted rounded"
            >
              {t('Cancel')}
            </button>
            <button
              onClick={handleAdd}
              disabled={submitting || !formData.description.trim()}
              className="px-3 py-1.5 text-xs bg-primary text-white rounded hover:bg-primary/90 disabled:opacity-50"
            >
              {submitting ? t('Adding...') : t('Add Blocker')}
            </button>
          </div>
        </div>
      )}

      {/* Active blockers */}
      {activeBlockers.length > 0 && (
        <div className="mb-4">
          <h4 className="text-sm font-semibold text-foreground mb-1.5">
            {t('Active Blockers')} ({activeBlockers.length})
          </h4>
          <div className="space-y-2">
            {activeBlockers.map((blocker) => (
              <div
                key={blocker.id}
                className="border border-border rounded-lg p-3"
                data-testid={`blocker-${blocker.id}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-start gap-2 flex-1">
                    <AppIcon name="AlertOctagon" size={16} color={severityColors[blocker.severity] || '#ef4444'} className="mt-0.5 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-foreground">
                        {blocker.description}
                      </p>
                      <div className="flex items-center gap-2 mt-1 flex-wrap">
                        <span
                          className="inline-flex items-center px-1.5 py-0.5 rounded text-[0.65rem] font-semibold"
                          style={{
                            backgroundColor: severityBg[blocker.severity] || '#fef2f2',
                            color: severityColors[blocker.severity] || '#ef4444',
                          }}
                        >
                          {t(blocker.severity)}
                        </span>
                        <span className="text-xs text-muted-foreground">
                          {blocker.createdByName} &middot; {formatDate(blocker.createdAt)}
                        </span>
                      </div>
                      {blocker.requiredResources && (
                        <div className="mt-1.5 flex items-center gap-1">
                          <AppIcon name="Package" size={12} color="var(--color-muted-foreground)" />
                          <span className="text-xs text-muted-foreground">
                            {blocker.requiredResources}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                  {canEdit && (
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <button
                        onClick={() => { setResolveId(blocker.id); setResolveNote(''); }}
                        className="p-1 hover:bg-success/10 rounded"
                        title={t('Resolve')}
                      >
                        <AppIcon name="CheckCircle" size={16} color="#10b981" />
                      </button>
                      <button
                        onClick={() => handleDelete(blocker.id)}
                        className="p-1 hover:bg-destructive/10 rounded"
                        title={t('Delete')}
                      >
                        <AppIcon name="Trash2" size={16} color="#ef4444" />
                      </button>
                    </div>
                  )}
                </div>

                {/* Resolve form */}
                {resolveId === blocker.id && (
                  <div className="mt-3 pt-3 border-t border-border space-y-2">
                    <textarea
                      value={resolveNote}
                      onChange={(e) => setResolveNote(e.target.value)}
                      className="w-full px-2 py-1.5 border border-border rounded bg-background text-foreground text-sm resize-none"
                      rows={2}
                      placeholder={t('Resolution note...')}
                    />
                    <div className="flex justify-end gap-2">
                      <button
                        onClick={() => setResolveId(null)}
                        className="px-2 py-1 text-xs text-muted-foreground hover:bg-muted rounded"
                      >
                        {t('Cancel')}
                      </button>
                      <button
                        onClick={() => handleResolve(blocker.id)}
                        disabled={submitting || !resolveNote.trim()}
                        className="px-2 py-1 text-xs bg-success text-white rounded hover:bg-success/90 disabled:opacity-50"
                      >
                        {submitting ? t('Resolving...') : t('Resolve')}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Resolved blockers */}
      {resolvedBlockers.length > 0 && (
        <div>
          <h4 className="text-sm font-semibold text-muted-foreground mb-1.5">
            {t('Resolved')} ({resolvedBlockers.length})
          </h4>
          <div className="space-y-2">
            {resolvedBlockers.map((blocker) => (
              <div
                key={blocker.id}
                className="border border-border rounded-lg p-3 opacity-60"
                data-testid={`blocker-resolved-${blocker.id}`}
              >
                <div className="flex items-start gap-2">
                  <AppIcon name="CheckCircle2" size={16} color="#10b981" className="mt-0.5 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground line-through">
                      {blocker.description}
                    </p>
                    <div className="flex items-center gap-2 mt-1 flex-wrap">
                      <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[0.65rem] font-semibold bg-success/10 text-success">
                        {t('Resolved')}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        {blocker.resolvedByName} &middot; {blocker.resolvedAt ? formatDate(blocker.resolvedAt) : ''}
                      </span>
                    </div>
                    {blocker.resolutionNote && (
                      <div className="mt-1.5 flex items-start gap-1">
                        <AppIcon name="MessageSquare" size={12} color="var(--color-muted-foreground)" className="mt-0.5" />
                        <span className="text-xs text-muted-foreground italic">
                          {blocker.resolutionNote}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Empty state */}
      {blockers.length === 0 && !showAddForm && (
        <div className="text-center py-4">
          <AppIcon name="Shield" size={32} color="var(--color-muted-foreground)" />
          <p className="text-sm text-muted-foreground mt-1">
            {t('No blockers reported')}
          </p>
        </div>
      )}
    </div>
  );
};

export default BlockerPanel;
